import { graphql } from 'react-relay';
import { useCallback, useState } from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type {
	useFolderChildrenQuery$data,
	useFolderChildrenQuery$variables,
} from './__generated__/useFolderChildrenQuery.graphql.ts';

const FOLDER_CHILDREN_PAGE_SIZE = 25;

type FolderChild = NonNullable<
	NonNullable<NonNullable<useFolderChildrenQuery$data['ptpage']>['children']>['nodes']
>[number];

type UseFolderChildrenResult = {
	folderChildren: ReadonlyArray<NonNullable<FolderChild>>;
	loading: boolean;
	error?: ApolloErrorInterop | Error;
	isLoadingMore: boolean;
	loadMore?: () => Promise<void>;
};

export const useFolderChildren = (
	folderId: string,
	shouldLoad: boolean,
): UseFolderChildrenResult => {
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [loadMoreError, setLoadMoreError] = useState<Error>();
	const { data, loading, error, fetchMore } = useQueryInterop<
		useFolderChildrenQuery$data,
		useFolderChildrenQuery$variables
	>(
		graphql`
			query useFolderChildrenQuery($folderId: ID!, $first: Int, $after: String) {
				ptpage(id: $folderId, status: [CURRENT]) {
					id
					children(first: $first, after: $after) {
						nodes {
							id
							title
							type
							subType
							hasChildren
						}
						pageInfo {
							endCursor
							hasNextPage
						}
					}
				}
			}
		`,
		{
			variables: {
				folderId,
				first: FOLDER_CHILDREN_PAGE_SIZE,
			},
			skip: !shouldLoad,
			notifyOnNetworkStatusChange: true,
			fetchPolicy: 'cache-first',
		},
	);

	const pageInfo = data?.ptpage?.children?.pageInfo;
	const canLoadMore = Boolean(pageInfo?.hasNextPage && pageInfo.endCursor);

	const loadMore = useCallback(async (): Promise<void> => {
		const endCursor = pageInfo?.endCursor;
		if (!endCursor || !canLoadMore || loading || isLoadingMore) {
			return;
		}

		setLoadMoreError(undefined);
		setIsLoadingMore(true);

		await fetchMore({
			variables: {
				after: endCursor,
			},
			updateQuery: (previous, { fetchMoreResult }) => {
				const previousPage = previous.ptpage?.children;
				const nextPage = fetchMoreResult?.ptpage?.children;
				if (!previous.ptpage || !previousPage || !nextPage) {
					return previous;
				}

				return {
					...previous,
					ptpage: {
						...previous.ptpage,
						children: {
							...nextPage,
							nodes: [...(previousPage.nodes ?? []), ...(nextPage.nodes ?? [])],
						},
					},
				};
			},
		})
			.catch((fetchError) => {
				setLoadMoreError(fetchError instanceof Error ? fetchError : new Error(String(fetchError)));
			})
			.finally(() => {
				setIsLoadingMore(false);
			});
	}, [canLoadMore, fetchMore, isLoadingMore, loading, pageInfo?.endCursor]);

	// On the render where `skip` flips to false, `useQueryInterop` still reports `loading: false`
	// with no data because its fetch effect has not run yet. Treat "expanded but nothing has come
	// back" as loading so consumers do not flash an empty state before the spinner appears.
	const hasResult = data !== undefined || error !== undefined;

	return {
		folderChildren:
			data?.ptpage?.children?.nodes?.filter(
				(node): node is NonNullable<typeof node> => node !== null,
			) ?? [],
		loading: shouldLoad && (!hasResult || (loading && !data?.ptpage?.children)),
		error: error ?? loadMoreError,
		isLoadingMore,
		loadMore: canLoadMore ? loadMore : undefined,
	};
};
